import { Suspense, useState } from 'react';
import { Link as RouterLink, NavLink, Outlet } from 'react-router';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CloseRounded from '@mui/icons-material/CloseRounded';
import DarkModeRounded from '@mui/icons-material/DarkModeRounded';
import DescriptionRounded from '@mui/icons-material/DescriptionRounded';
import GridViewRounded from '@mui/icons-material/GridViewRounded';
import HomeRounded from '@mui/icons-material/HomeRounded';
import KeyboardDoubleArrowLeftRounded from '@mui/icons-material/KeyboardDoubleArrowLeftRounded';
import KeyboardDoubleArrowRightRounded from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import LightModeRounded from '@mui/icons-material/LightModeRounded';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import ManageSearchRounded from '@mui/icons-material/ManageSearchRounded';
import MenuRounded from '@mui/icons-material/MenuRounded';
import PersonRounded from '@mui/icons-material/PersonRounded';
import { useTranslation } from 'react-i18next';
import { applyLanguage, applyScheme, BrandLogo, ConsentBanner, currentScheme } from '@fms/ui';
import { DEFAULT_BRANDING, resolveI18n } from '@fms/contracts';
import { useBranding, useLogout } from '@fms/api-client';
import i18n from '../i18n';
import { useSession } from '../session';

const DRAWER_WIDTH = 248;
const DRAWER_COLLAPSED = 76;
const NAV_KEY = 'fms.citizen.nav';

interface NavItem {
  key: string;
  to: string;
  icon: React.ReactNode;
  labelKey: string;
  authed?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { key: 'home', to: '/home', icon: <HomeRounded />, labelKey: 'nav.home', authed: true },
  // The catalogue is behind the login now, so it is not offered to anonymous
  // visitors — only Track is (see router.tsx).
  {
    key: 'services',
    to: '/services',
    icon: <GridViewRounded />,
    labelKey: 'nav.services',
    authed: true,
  },
  {
    key: 'applications',
    to: '/applications',
    icon: <DescriptionRounded />,
    labelKey: 'nav.applications',
    authed: true,
  },
  { key: 'track', to: '/track', icon: <ManageSearchRounded />, labelKey: 'nav.track' },
  {
    key: 'profile',
    to: '/profile',
    icon: <PersonRounded />,
    labelKey: 'nav.profile',
    authed: true,
  },
];

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

export function Layout() {
  const { t } = useTranslation();
  const { user } = useSession();
  const logout = useLogout();
  const { data: brandingData } = useBranding();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => typeof localStorage !== 'undefined' && localStorage.getItem(NAV_KEY) === 'collapsed',
  );
  const [scheme, setScheme] = useState(currentScheme);

  const toggleTheme = () => {
    const next = currentScheme() === 'dark' ? 'light' : 'dark';
    applyScheme(next);
    setScheme(next);
  };
  const toggleLang = () => {
    const next = i18n.language === 'hi' ? 'en' : 'hi';
    void i18n.changeLanguage(next);
    applyLanguage(next);
  };
  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      localStorage.setItem(NAV_KEY, prev ? 'open' : 'collapsed');
      return !prev;
    });
  };
  const onLogout = () => {
    logout.mutate(undefined, {
      // Hard navigation, not router.navigate: the token teardown re-renders
      // the old route's auth guard, whose login redirect races (and loses)
      // any SPA navigation scheduled alongside it. A document load lands on
      // the login screen deterministically, with every cache dropped.
      onSettled: () => window.location.assign('/auth/login'),
    });
  };

  const items = NAV_ITEMS.filter((item) => !item.authed || Boolean(user));
  const railWidth = collapsed && isDesktop ? DRAWER_COLLAPSED : DRAWER_WIDTH;
  // Fall back to the shipped defaults, not to separate locale strings: this
  // renders for the one paint before GET /branding resolves, and a second copy
  // of the name drifts from the API's the moment either is edited. They are
  // already {en, hi} maps, so resolveI18n covers the language either way.
  const branding = brandingData?.branding ?? DEFAULT_BRANDING;
  const appName = resolveI18n(branding.appName, i18n.language);
  const orgName = resolveI18n(branding.orgName, i18n.language);

  const rail = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        // The rail follows the scheme now — white in light, black in dark — so it
        // needs its own edge against an identically-coloured canvas.
        bgcolor: 'var(--fms-color-sidebar)',
        color: 'var(--fms-color-sidebar-text)',
        borderRight: '1px solid var(--fms-color-sidebar-divider)',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 2, py: 2 }}>
        <BrandLogo
          image={branding?.logo}
          imageDark={branding?.logoDark}
          {...(brandingData ? { assetBaseUrl: brandingData.assetBaseUrl } : {})}
          size={36}
          // Named for screen readers — the org's own name, not decorative
          // silence, so focus/browse never mislabels it (BTWR-775).
          alt={t('nav.logoAlt', { org: orgName })}
        />
        {!collapsed || !isDesktop ? (
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              noWrap
              sx={{
                color: 'var(--fms-color-sidebar-text-strong)',
                fontWeight: 800,
                fontSize: 14,
                // 1.2 clipped the tops of Devanagari matras (Noto Sans
                // Devanagari's ascenders sit taller than Roboto's) — 1.4 gives
                // Hindi labels room without visibly changing the Latin case.
                lineHeight: 1.4,
              }}
            >
              {appName}
            </Typography>
            {/* Dim rail color fails 4.5:1 at caption sizes — use full rail text. */}
            <Typography
              noWrap
              sx={{ fontSize: 10.5, lineHeight: 1.4, color: 'var(--fms-color-sidebar-text)' }}
            >
              {orgName}
            </Typography>
          </Box>
        ) : null}
        {!isDesktop ? (
          // Explicit close for the phone drawer — tapping the backdrop works
          // but is undiscoverable, and the drawer covers most of the screen.
          <IconButton
            onClick={() => setMobileOpen(false)}
            aria-label={t('nav.closeMenu')}
            size="small"
            sx={{ color: 'inherit', flexShrink: 0 }}
          >
            <CloseRounded />
          </IconButton>
        ) : null}
      </Stack>

      <List component="nav" aria-label={t('nav.main')} sx={{ px: 1.25, flex: 1 }}>
        {items.map((item) => (
          <Tooltip
            key={item.key}
            title={collapsed && isDesktop ? t(item.labelKey) : ''}
            placement="right"
          >
            <ListItemButton
              component={NavLink}
              to={item.to}
              onClick={() => setMobileOpen(false)}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                color: 'inherit',
                minHeight: 44,
                justifyContent: collapsed && isDesktop ? 'center' : 'flex-start',
                '& .MuiListItemIcon-root': {
                  color: 'inherit',
                  minWidth: collapsed && isDesktop ? 0 : 38,
                },
                '&.active': {
                  bgcolor: 'var(--fms-color-gold)',
                  color: 'var(--fms-color-on-gold)',
                  boxShadow: '0 4px 12px rgb(212 160 23 / 0.35)',
                  '& .MuiListItemText-primary': { fontWeight: 700 },
                  '&:hover': { bgcolor: 'var(--fms-color-gold-deep)' },
                },
                '&:hover': {
                  color: 'var(--fms-color-sidebar-text-strong)',
                  bgcolor: 'var(--fms-color-sidebar-hover)',
                },
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              {!collapsed || !isDesktop ? (
                <ListItemText
                  primary={t(item.labelKey)}
                  slotProps={{ primary: { sx: { fontSize: 13.5, fontWeight: 500 } } }}
                />
              ) : null}
            </ListItemButton>
          </Tooltip>
        ))}
      </List>

      <Box
        sx={{ px: 1.25, pb: 1.5, borderTop: '1px solid var(--fms-color-sidebar-divider)', pt: 1.5 }}
      >
        {isDesktop ? (
          <ListItemButton
            onClick={toggleCollapsed}
            sx={{
              borderRadius: 2,
              color: 'inherit',
              minHeight: 44,
              justifyContent: collapsed ? 'center' : 'flex-start',
              '& .MuiListItemIcon-root': { color: 'inherit', minWidth: collapsed ? 0 : 38 },
              '&:hover': {
                color: 'var(--fms-color-sidebar-text-strong)',
                bgcolor: 'var(--fms-color-sidebar-hover)',
              },
            }}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          >
            <ListItemIcon>
              {collapsed ? <KeyboardDoubleArrowRightRounded /> : <KeyboardDoubleArrowLeftRounded />}
            </ListItemIcon>
            {!collapsed ? (
              <ListItemText
                primary="Collapse"
                slotProps={{ primary: { sx: { fontSize: 13.5, fontWeight: 500 } } }}
              />
            ) : null}
          </ListItemButton>
        ) : null}
        {user && (!collapsed || !isDesktop) ? (
          <ListItemButton
            component={NavLink}
            to="/profile"
            onClick={() => setMobileOpen(false)}
            aria-label={t('nav.profile')}
            sx={{
              borderRadius: 2,
              mt: 0.5,
              px: 1,
              py: 0.75,
              gap: 1.25,
              color: 'inherit',
              '&:hover': {
                color: 'var(--fms-color-sidebar-text-strong)',
                bgcolor: 'var(--fms-color-sidebar-hover)',
              },
            }}
          >
            <Avatar
              sx={{
                width: 34,
                height: 34,
                fontSize: 13,
                fontWeight: 700,
                bgcolor: 'var(--fms-color-gold)',
                // MUI's Avatar defaults its ink to background.default, which on a
                // white canvas would be white-on-gold — spell out the dark ink.
                color: 'var(--fms-color-on-gold)',
              }}
            >
              {initials(user.fullName)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  color: 'var(--fms-color-sidebar-text-strong)',
                  fontSize: 12.5,
                  fontWeight: 600,
                }}
              >
                {user.fullName}
              </Typography>
              <Typography sx={{ fontSize: 10.5, color: 'var(--fms-color-sidebar-text)' }}>
                {user.mobile ?? user.email}
              </Typography>
            </Box>
          </ListItemButton>
        ) : null}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      {/* Focused, this used to appear as a bare link on top of the rail logo —
          keyboard users heard "skip to content" while looking at the logo
          (BTWR-775). Styled as its own floating pill so it reads as a control
          in its own right, and translated like everything else. */}
      <Box
        component="a"
        href="#main"
        sx={{
          position: 'absolute',
          left: -9999,
          top: 8,
          zIndex: 2000,
          px: 1.5,
          py: 1,
          borderRadius: 1.5,
          bgcolor: 'background.paper',
          color: 'primary.main',
          fontWeight: 600,
          fontSize: 14,
          textDecoration: 'none',
          border: 1,
          borderColor: 'divider',
          boxShadow: 3,
          '&:focus-visible': { left: 8 },
        }}
      >
        {t('nav.skipToContent')}
      </Box>

      <Drawer
        variant={isDesktop ? 'permanent' : 'temporary'}
        open={isDesktop ? true : mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: railWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isDesktop ? railWidth : DRAWER_WIDTH,
            border: 0,
            transition: 'width .18s ease',
            overflowX: 'hidden',
          },
        }}
      >
        {rail}
      </Drawer>

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <AppBar
          position="sticky"
          color="inherit"
          elevation={0}
          sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
        >
          <Toolbar sx={{ gap: { xs: 0.5, sm: 1 } }}>
            {!isDesktop ? (
              <IconButton
                onClick={() => setMobileOpen(true)}
                aria-label={t('nav.menu')}
                edge="start"
              >
                <MenuRounded />
              </IconButton>
            ) : null}
            <Stack
              component={RouterLink}
              to={user ? '/home' : '/services'}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                textDecoration: 'none',
                color: 'inherit',
                display: { xs: 'flex', md: 'none' },
                minWidth: 0,
              }}
            >
              <BrandLogo
                image={branding?.logo}
                imageDark={branding?.logoDark}
                {...(brandingData ? { assetBaseUrl: brandingData.assetBaseUrl } : {})}
                size={28}
              />
              <Typography noWrap sx={{ fontWeight: 700, fontSize: 15 }}>
                {appName}
              </Typography>
            </Stack>
            <Box sx={{ flex: 1 }} />
            {branding.showLanguageSwitcher ? (
              <Button onClick={toggleLang} size="small" sx={{ minWidth: { xs: 44, sm: 64 } }}>
                {t('lang.toggle')}
              </Button>
            ) : null}
            <IconButton onClick={toggleTheme} aria-label={t('theme.toggle')}>
              {scheme === 'dark' ? <LightModeRounded /> : <DarkModeRounded />}
            </IconButton>
            {user ? (
              <>
                {/* Icon-only on phones — the labelled button wraps to two lines
                    and squeezes the brand out of a 360px toolbar. */}
                <Tooltip title={t('nav.logout')}>
                  <IconButton
                    onClick={onLogout}
                    aria-label={t('nav.logout')}
                    sx={{
                      display: { xs: 'inline-flex', sm: 'none' },
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 2,
                    }}
                  >
                    <LogoutRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Button
                  onClick={onLogout}
                  variant="outlined"
                  size="small"
                  startIcon={<LogoutRounded />}
                  sx={{ display: { xs: 'none', sm: 'inline-flex' }, whiteSpace: 'nowrap' }}
                >
                  {t('nav.logout')}
                </Button>
              </>
            ) : (
              <Button
                component={RouterLink}
                to="/auth/login"
                variant="contained"
                size="small"
                sx={{ whiteSpace: 'nowrap' }}
              >
                {t('nav.login')}
              </Button>
            )}
          </Toolbar>
        </AppBar>

        <Container
          component="main"
          id="main"
          maxWidth="lg"
          sx={{ flex: 1, py: { xs: 2.5, md: 4 } }}
        >
          <Suspense fallback={<LinearProgress aria-label={t('common.loading')} />}>
            <Outlet />
          </Suspense>
        </Container>

        <Box
          component="footer"
          sx={{
            // Pinned to the viewport bottom while the page scrolls (it rests
            // in place once the true end is reached). Slim on purpose — a
            // permanently visible strip must not eat into a phone's viewport.
            position: 'sticky',
            bottom: 0,
            zIndex: (theme) => theme.zIndex.appBar - 1,
            borderTop: 1,
            borderColor: 'divider',
            pt: 1.25,
            pb: 'max(env(safe-area-inset-bottom), 10px)',
            textAlign: 'center',
            color: 'text.secondary',
            // Glass over the scrolling content, same surface as the other
            // sticky strips — an unpainted footer would let text collide.
            bgcolor: 'var(--fms-color-glass)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Typography variant="body2" sx={{ fontSize: 12.5 }}>
            {appName} · {orgName}
          </Typography>
        </Box>
      </Box>

      <ConsentBanner
        labels={{
          message: t('consent.message'),
          accept: t('consent.accept'),
          decline: t('consent.decline'),
        }}
      />
    </Box>
  );
}
